import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Phone } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled");
  const [status, setStatus] = useState(cancelled ? "cancelled" : "checking");
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId || attempts >= 5) {
      if (attempts >= 5) setStatus("timeout");
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setStatus("paid");
        return;
      } else if (res.data.status === "expired") {
        setStatus("expired");
        return;
      }
      setAttempts((a) => a + 1);
      setTimeout(() => pollStatus(), 2000);
    } catch {
      setAttempts((a) => a + 1);
      setTimeout(() => pollStatus(), 2000);
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (cancelled) return;
    if (sessionId) pollStatus();
    else setStatus("error");
  }, []);

  return (
    <div className="min-h-screen bg-[#0B110E] flex items-center justify-center px-4" data-testid="payment-result-page">
      <div className="max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-[#D95D39] mx-auto mb-4 animate-spin" />
            <h1 className="text-[#F8FAFC] font-black text-2xl tracking-tighter mb-2">Processing Payment...</h1>
            <p className="text-[#9CA3AF] text-sm">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 bg-[#16A34A]/10 border border-[#16A34A]/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#16A34A]" />
            </div>
            <h1 className="text-[#F8FAFC] font-black text-2xl tracking-tighter mb-2" data-testid="payment-success">Payment Successful!</h1>
            <p className="text-[#9CA3AF] text-sm mb-6">Thank you for your payment. Your invoice has been marked as paid.</p>
          </>
        )}
        {status === "cancelled" && (
          <>
            <div className="w-16 h-16 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-[#F59E0B]" />
            </div>
            <h1 className="text-[#F8FAFC] font-black text-2xl tracking-tighter mb-2">Payment Cancelled</h1>
            <p className="text-[#9CA3AF] text-sm mb-6">The payment was cancelled. If this was a mistake, contact us.</p>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <div className="w-16 h-16 bg-[#DC2626]/10 border border-[#DC2626]/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-[#DC2626]" />
            </div>
            <h1 className="text-[#F8FAFC] font-black text-2xl tracking-tighter mb-2">Payment Issue</h1>
            <p className="text-[#9CA3AF] text-sm mb-6">There was a problem verifying your payment. Please contact us.</p>
          </>
        )}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4">
          <Link to="/" className="text-[#9CA3AF] text-sm hover:text-[#D95D39] transition-colors duration-150">&larr; Back to Website</Link>
          <a href="tel:9042376253" className="bg-[#D95D39] hover:bg-[#c04d2d] text-white font-bold text-sm px-5 py-2.5 rounded-sm inline-flex items-center gap-2 transition-colors duration-150">
            <Phone className="w-4 h-4" /> Call 904-237-6253
          </a>
        </div>
      </div>
    </div>
  );
}
